import { call, put, select, all, takeLatest } from 'redux-saga/effects';
import { comicActionTypes as types } from 'src/redux/actions/comicActions';
import { ref as dbRef, get, set, push, update, remove } from "firebase/database";
import { ref as stoRef, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import notification from 'src/utils/notification';
import { removeEmptyFields, toNullPropsObj, deleteFileRecursively } from 'src/utils/helpers';
import adminSelectors from 'src/redux/selectors/adminSelectors';
import { database, storage } from 'src/firebase';

function* fetchComics({ payload, callback }) {
  try {
    const comicsDbRef = dbRef(database, 'comics');
    const data = yield get(comicsDbRef);
    if (!data.exists()) {
      throw new Error('No data!');
    }
    yield put({ type: types.FETCH_COMICS_SUCCESS, data: data.val() });
    notification.close();
    callback?.();
  } catch (error) {
    yield put({ type: types.FETCH_COMICS_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* addComic({ payload, callback }) {
  /*
    Explain flow:
    1.generate key/id for comic to append to refs
    2.upload cover to storage
    3.append url of uploaded cover to data and send appended data to realtime database
  */
  try {
    const admin = yield select(adminSelectors.admin);
    const data = removeEmptyFields(payload);
    const rootDbRef = dbRef(database);
    const { key } = yield push(rootDbRef);  // generate key/id using push of firebase

    // upload cover
    const coverStoRef = stoRef(storage, `comics/${key}/cover.jpg`);
    const coverUpload = yield uploadBytes(coverStoRef, data.cover.originFileObj);
    const coverUrl = yield getDownloadURL(coverUpload.ref);

    // set comic data to database
    const comicsDbRef = dbRef(database, `comics/${key}`);
    yield set(comicsDbRef, {
      ...data,
      id: key,
      cover: coverUrl,
      createdAt: Date.now(),
      createdBy: admin.email,
      chapterCount: 0,
      views: 0,
      rating: {
        average: 0
      }
    });

    yield put({ type: types.ADD_COMIC_SUCCESS });
    notification.close();
    notification.showSuccess('Success', 3);
    callback?.();
  } catch (error) {
    yield put({ type: types.ADD_COMIC_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* editComic({ payload, callback }) {
  try {
    const admin = yield select(adminSelectors.admin);
    const data = removeEmptyFields(payload);
    const { comicId, cover } = data;

    // upload cover
    let coverUrl;
    if (cover) {  // check if cover is updated or not
      const coverStoRef = stoRef(storage, `comics/${comicId}/cover.jpg`);
      const coverUpload = yield uploadBytes(coverStoRef, cover.originFileObj);
      coverUrl = yield getDownloadURL(coverUpload.ref);
    }

    // update comic data to database
    const comicsDbRef = dbRef(database, `comics/${comicId}`);
    yield update(comicsDbRef, {
      ...data,
      ...(coverUrl && { cover: coverUrl }),
      updatedAt: Date.now(),
      updatedBy: admin.email
    });

    yield put({ type: types.EDIT_COMIC_SUCCESS });
    notification.close();
    notification.showSuccess('Success', 3);
    callback?.();
  } catch (error) {
    yield put({ type: types.EDIT_COMIC_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* deleteComics({ payload, callback }) {
  try {
    yield all(
      payload.map(async (comicId) => {
        const comicStoRef = stoRef(storage, `comics/${comicId}`);
        const comicDbRef = dbRef(database, `comics/${comicId}`);
        const chapterDbRef = dbRef(database, `chapters/${comicId}`);

        deleteFileRecursively(comicStoRef);
        remove(comicDbRef);
        remove(chapterDbRef);
      })
    );

    yield put({ type: types.DELETE_COMICS_SUCCESS });
    notification.close();
    notification.showSuccess('Success', 3);
    callback?.();
  } catch (error) {
    yield put({ type: types.DELETE_COMICS_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

export default function* comicSaga() {
  yield all([
    takeLatest(types.FETCH_COMICS_REQUEST, fetchComics),
    takeLatest(types.ADD_COMIC_REQUEST, addComic),
    takeLatest(types.EDIT_COMIC_REQUEST, editComic),
    takeLatest(types.DELETE_COMICS_REQUEST, deleteComics),
  ])
}
